import {
  Button,
  Input,
  InputMasked,
  LoadingPage,
  NotFound,
} from '@chiroup/components';
import React from 'react';

import ValidEmailAlert from '../common/ValidEmailAlert';
import { EmailStatus, FormStates } from './Seminar';
import SeminarClinicForm from './SeminarClinicForm';
import SeminarClinicFormFields from './SeminarClinicFormFields';
import { FormErrors } from '@chiroup/core/types/ErrorResponse.type';
import {
  SeminarSignupForm,
  SeminarMeData,
} from '@chiroup/core/types/Seminar.type';
import { ValueOf } from '@visx/scale';

type Props = {
  value: Partial<SeminarSignupForm>;
  onChange: (
    key: keyof Partial<SeminarSignupForm>,
  ) => (val: ValueOf<SeminarSignupForm>) => void;
  errors?: FormErrors;
  formState: FormStates;
  emailStatus: EmailStatus;
  isSubmitting: boolean;
  sendNewCode: () => void;
  token?: string;
  userData?: SeminarMeData;
  setUserData: React.Dispatch<
    React.SetStateAction<
      { seminarMe: SeminarMeData; token: string } | undefined
    >
  >;
  onSubmit: ({
    email,
    validationCode,
  }: Partial<SeminarSignupForm>) => Promise<Partial<SeminarSignupForm>>;
};

const SeminarForm: React.FC<Props> = ({
  value,
  onChange,
  errors,
  formState,
  emailStatus,
  isSubmitting,
  sendNewCode,
  token,
  userData,
  setUserData,
  onSubmit,
}) => {
  switch (formState) {
    case FormStates.CheckEmailValidity:
      return (
        <div className="flex flex-col">
          <div className="flex flex-col space-y-2 md:space-y-4">
            <div>
              <Input
                name="email"
                value={value.email}
                onChange={onChange('email')}
                label="Email *"
                className="max-w-md pb-2"
                errors={errors?.fieldErrors?.email}
              />
              {emailStatus === EmailStatus.Invalid && (
                <ValidEmailAlert styles="max-w-md" />
              )}
            </div>
            <Button
              text="Next"
              type="submit"
              className="mt-4"
              loading={isSubmitting}
              disabled={!value.email}
            />
          </div>
        </div>
      );
    case FormStates.ValidationCode:
      return (
        <div className="flex flex-col">
          <p className="font-light text-md">
            Please check your email and enter your validation code below.
          </p>
          <InputMasked
            value={value.validationCode}
            name="validationCode"
            onChange={onChange('validationCode')}
            label="Validation Code *"
            className="max-w-md pb-2"
            // options={{ numericOnly: true, blocks: [6] }}
            hint={
              <span className="text-sm">
                Didn't get a code?{' '}
                <span
                  className="font-bold cursor-pointer text-primary-500"
                  onClick={sendNewCode}
                >
                  Click here to resend.
                </span>
              </span>
            }
            errors={errors?.fieldErrors?.validationCode}
          />
          <Button
            text="Next"
            type="submit"
            className="mt-4"
            loading={isSubmitting}
            disabled={value.validationCode?.toString().length !== 6}
          />
        </div>
      );
    case FormStates.UserForm:
      if (!token) return null;
      return userData?.user?.ID ? (
        <div className="flex flex-col">
          <SeminarClinicForm
            userData={userData}
            onChange={onChange}
            value={value}
            errors={errors}
            isSubmitting={isSubmitting}
            setUserData={setUserData}
            onSubmit={onSubmit}
          />
          <Button
            text="Next"
            type="submit"
            className="mt-4"
            loading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      ) : (
        <LoadingPage />
      );
    case FormStates.NotAUserForm:
      return (
        <div className="flex flex-col">
          <div className="flex flex-col md:flex-row md:space-x-4">
            <div className="pb-2 md:w-1/2">
              <Input
                name="fname"
                value={value.fname}
                onChange={onChange('fname')}
                label="First Name *"
                errors={errors?.fieldErrors?.fname}
              />
            </div>
            <div className="pb-2 md:w-1/2">
              <Input
                name="lname"
                value={value.lname}
                onChange={onChange('lname')}
                label="Last Name *"
                errors={errors?.fieldErrors?.lname}
              />
            </div>
          </div>
          <SeminarClinicFormFields
            value={value}
            onChange={onChange}
            errors={errors}
          />
          <Button
            text="Next"
            type="submit"
            className="mt-4"
            loading={isSubmitting}
            disabled={isSubmitting}
          />
        </div>
      );
    default:
      return <NotFound />;
  }
};

export default SeminarForm;
